import React, { useState, useRef, useEffect } from 'react';
import { ChevronRight, ChevronDown, Copy, Check, Maximize2, Minimize2 } from 'lucide-react';

/**
 * JsonViewer
 * -----------------------------------------------------------------------
 * A collapsible JSON tree viewer built with React + Tailwind CSS.
 *
 * - Every object/array node is collapsed by default.
 * - Click anywhere on a row (or its chevron) to expand/collapse it.
 * - "Expand all" / "Collapse all" controls operate on the whole tree.
 * - "Copy" copies the full JSON (pretty-printed) to the clipboard.
 *
 * Props:
 *   data            any JSON-serializable value (default: sample data)
 *   title           label shown in the header bar (default: "JSON")
 *   defaultExpanded whether nodes start expanded (default: false)
 *
 * Usage:
 *   <JsonViewer data={apiResponse} title="API Response" />
 *
 * Requires: lucide-react
 * -----------------------------------------------------------------------
 */

const getType = (value) => {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
};

const getPreview = (value, type) => {
  if (type === 'array') {
    const n = value.length;
    return n === 0 ? '' : `${n} ${n === 1 ? 'item' : 'items'}`;
  }
  if (type === 'object') {
    const n = Object.keys(value).length;
    return n === 0 ? '' : `${n} ${n === 1 ? 'key' : 'keys'}`;
  }
  return '';
};

function ValuePreview({ value, type }) {
  switch (type) {
    case 'string':
      return <span className="text-emerald-600">"{value}"</span>;
    case 'number':
      return <span className="text-sky-600">{value}</span>;
    case 'boolean':
      return <span className="text-amber-600">{value ? 'true' : 'false'}</span>;
    case 'null':
    case 'undefined':
      return <span className="text-slate-400 italic">null</span>;
    default:
      return <span className="text-slate-600">{String(value)}</span>;
  }
}

function JsonNode({ name, value, depth, isLast, expandSignal }) {
  const type = getType(value);
  const isExpandable =
    (type === 'object' || type === 'array') && Object.keys(value || {}).length > 0;

  const [expanded, setExpanded] = useState(expandSignal.value);
  const lastSignalId = useRef(expandSignal.id);

  useEffect(() => {
    if (expandSignal.id !== lastSignalId.current) {
      lastSignalId.current = expandSignal.id;
      setExpanded(expandSignal.value);
    }
  }, [expandSignal.id, expandSignal.value]);

  const indent = { paddingLeft: `${depth * 16}px` };

  const renderKey = () => {
    if (name === undefined) return null;
    if (typeof name === 'number') {
      return <span className="text-slate-400 mr-1">{name}:</span>;
    }
    return (
      <span className="mr-1">
        <span className="text-violet-600">"{name}"</span>
        <span className="text-slate-400">:</span>
      </span>
    );
  };

  // Leaf values (strings, numbers, booleans, null, empty object/array)
  if (!isExpandable) {
    const empty = type === 'object' ? '{}' : type === 'array' ? '[]' : null;
    return (
      <div
        className="flex items-start font-mono text-[13px] leading-6 hover:bg-slate-50 rounded px-1 -mx-1"
        style={indent}
      >
        <span className="inline-block w-4 mr-1 flex-shrink-0" />
        {renderKey()}
        {empty ? (
          <span className="text-slate-400">{empty}</span>
        ) : (
          <ValuePreview value={value} type={type} />
        )}
        {!isLast && <span className="text-slate-400">,</span>}
      </div>
    );
  }

  // Expandable nodes (non-empty objects/arrays)
  const entries = type === 'array' ? value.map((v, i) => [i, v]) : Object.entries(value);
  const openBracket = type === 'array' ? '[' : '{';
  const closeBracket = type === 'array' ? ']' : '}';

  return (
    <div className="font-mono text-[13px]">
      <div
        className="flex items-start leading-6 cursor-pointer hover:bg-slate-50 rounded px-1 -mx-1 select-none"
        style={indent}
        onClick={() => setExpanded((e) => !e)}
      >
        <span className="w-4 mr-1 flex-shrink-0 text-slate-400 mt-0.5">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
        {renderKey()}
        <span className="text-slate-400">{openBracket}</span>
        {!expanded && (
          <>
            <span className="text-slate-400 italic mx-1.5 text-xs">
              {getPreview(value, type)}
            </span>
            <span className="text-slate-400">{closeBracket}</span>
            {!isLast && <span className="text-slate-400">,</span>}
          </>
        )}
      </div>

      {expanded && (
        <>
          {entries.map(([k, v], idx) => (
            <JsonNode
              key={k}
              name={k}
              value={v}
              depth={depth + 1}
              isLast={idx === entries.length - 1}
              expandSignal={expandSignal}
            />
          ))}
          <div className="font-mono text-[13px] leading-6 text-slate-400" style={indent}>
            <span className="inline-block w-4 mr-1" />
            {closeBracket}
            {!isLast && ','}
          </div>
        </>
      )}
    </div>
  );
}

export default function JsonViewer({ data, title = 'JSON', defaultExpanded = false }) {
  const [expandSignal, setExpandSignal] = useState({ id: 0, value: defaultExpanded });
  const [copied, setCopied] = useState(false);

  const handleExpandAll = () => setExpandSignal((s) => ({ id: s.id + 1, value: true }));
  const handleCollapseAll = () => setExpandSignal((s) => ({ id: s.id + 1, value: false }));

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API unavailable in this environment
    }
  };

  return (
    <div className="h-[stretch] w-full max-w-2xl mx-auto bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      {title !== '' && <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-50">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
          {title}
        </span>
        <div className="flex items-center gap-1.5">
          <button
            onClick={handleExpandAll}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded-md text-slate-600 hover:bg-slate-200/70 transition-colors"
          >
            <Maximize2 size={12} />
            Expand all
          </button>
          <button
            onClick={handleCollapseAll}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded-md text-slate-600 hover:bg-slate-200/70 transition-colors"
          >
            <Minimize2 size={12} />
            Collapse all
          </button>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded-md text-slate-600 hover:bg-slate-200/70 transition-colors"
          >
            {copied ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>}
      <div className="p-3 overflow-auto max-h-[28rem]">
        <JsonNode value={data} depth={0} isLast={true} expandSignal={expandSignal} />
      </div>
    </div>
  );
}
